import { Icon, Text } from '@southfields-digital/mpxlive-components';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';

import { StateType } from 'src/redux/reducers';
import { getPlaylistMode } from 'src/utils/playlist';

function MosBadge() {
  return (
    <span className="font-mono text-xs font-bold text-foreground shrink-0">
      MOS
    </span>
  );
}

function PlaylistBadge({ playlist }: { playlist: Playlist }) {
  switch (getPlaylistMode(playlist)) {
    case 'mos':
      return <MosBadge />;
    default:
      return (
        <span className="text-[10px] text-gray-500 shrink-0">
          {playlist.graphicCount} items
        </span>
      );
  }
}

function PlaylistOptionLabel({ playlist }: { playlist: Playlist }) {
  return (
    <div className="flex items-center justify-between gap-2 w-full">
      <span className="truncate">{playlist.name}</span>
      <PlaylistBadge playlist={playlist} />
    </div>
  );
}

type PlaylistSelectorProps = {
  value: string | null;
  onChange: (playlistId: string) => void;
  onAdd?: () => void;
};

const VIEWPORT_PADDING = 8;
const MIN_SCROLL_HEIGHT = 64;

export default function PlaylistSelector({ value, onChange, onAdd }: PlaylistSelectorProps) {
  const { data: playlists } = useSelector((state: StateType) => state.playlist.getAll);

  const selectedPlaylist = playlists?.find((p: Playlist) => p.id === value);

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({ position: 'fixed', visibility: 'hidden' });

  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setPanelStyle({ position: 'fixed', visibility: 'hidden' });
  }, []);

  const applyPosition = useCallback(() => {
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;

    const rect = triggerEl.getBoundingClientRect();
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const ph = panelRef.current?.offsetHeight ?? 0;

    const top = rect.bottom;
    const spaceBelow = vh - VIEWPORT_PADDING - top;
    const needsClamp = top + ph > vh - VIEWPORT_PADDING;

    const style: React.CSSProperties = {
      position: 'fixed',
      visibility: 'visible',
      top,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    };

    if (needsClamp) {
      style.maxHeight = Math.max(MIN_SCROLL_HEIGHT, spaceBelow);
      style.overflowY = 'auto';
    }

    setPanelStyle(style);
  }, []);

  // Position the panel when open
  useLayoutEffect(() => {
    if (!isOpen) return;
    applyPosition();

    const panelEl = panelRef.current;
    if (!panelEl || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => applyPosition());
    ro.observe(panelEl);
    return () => ro.disconnect();
  }, [isOpen, applyPosition, query]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener('scroll', applyPosition, true);
    window.addEventListener('resize', applyPosition);
    return () => {
      window.removeEventListener('scroll', applyPosition, true);
      window.removeEventListener('resize', applyPosition);
    };
  }, [isOpen, applyPosition]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, close]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, close]);

  const handleOpen = () => {
    if (!isOpen) {
      setIsOpen(true);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const handleSelect = (playlistId: string) => {
    onChange(playlistId);
    close();
  };

  const filteredPlaylists = playlists?.filter((p: Playlist) => {
    if (!query) return true;
    return p.name.toLowerCase().includes(query.toLowerCase());
  }) ?? [];

  const panel = isOpen
    ? ReactDOM.createPortal(
      <div
        ref={panelRef}
        style={panelStyle}
        className="rounded-b bg-[#191a26] border border-t-0 border-[rgba(249,250,251,0.15)] shadow-[0px_0px_12px_rgba(0,0,0,0.4)] [clip-path:inset(0px_-12px_-12px_-12px)] overflow-x-hidden"
      >
        <ul className="flex flex-col pb-1">
          {filteredPlaylists.map((playlist: Playlist) => (
            <li
              key={playlist.id}
              className="flex items-center gap-2 px-3 h-7 text-white cursor-pointer transition-colors duration-100 hover:bg-[#323342]"
              onClick={() => handleSelect(playlist.id)}
            >
              <span className="flex-1 text-sm font-medium">
                <PlaylistOptionLabel playlist={playlist} />
              </span>
            </li>
          ))}
          {filteredPlaylists.length === 0 && (
            <li className="px-3 h-7 flex items-center text-sm text-gray-500">No playlists found</li>
          )}
        </ul>
      </div>,
      document.body,
    )
    : null;

  return (
    <div className="flex items-center gap-2 px-3 py-2 shrink-0">
      <div ref={triggerRef} className="flex-1" onClick={handleOpen}>
        <div className={`group flex items-center gap-2 px-3 h-8 bg-[#191a26] border border-[rgba(249,250,251,0.15)] cursor-pointer ${isOpen ? 'rounded-t border-b-transparent' : 'rounded'}`}>
          <Icon icon="search" size={14} weight="bold" className="text-gray-500 shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
          {isOpen ? (
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={selectedPlaylist?.name || 'Search playlists...'}
              className="w-full bg-transparent text-sm text-foreground placeholder-gray-500 outline-none"
            />
          ) : (
            <>
              <Text className="text-sm text-foreground truncate flex-1">
                {selectedPlaylist?.name || (
                  <span className="text-gray-500">Select playlist</span>
                )}
              </Text>
              {selectedPlaylist && getPlaylistMode(selectedPlaylist) === 'mos' && <MosBadge />}
            </>
          )}
        </div>
      </div>
      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="flex items-center justify-center h-7 w-7 bg-[#323342] rounded-[2px] text-white hover:bg-[#3e3f52] transition-colors shrink-0 cursor-pointer"
          title="Add playlist"
        >
          <Icon icon="plus" size={16} weight="bold" />
        </button>
      )}
      {panel}
    </div>
  );
}
